<template>
    <div class="payable-receivable-mark-installments-container">
        <Loading v-if="loading" />

        <div v-else>
            <Alert type="info">
                <span>Parcelas em aberto: <b>{{ totalOpen }}</b></span>
            </Alert>

            <b-row>
                <b-col xs="12" sm="12" md="6">
                    <InputNumber title="Quantidade de parcelas" field="quantity" type="int" :formName="formName"
                        :required="true" :disabled="!totalOpen" v-model="quantity" />
                    <span class="payable-receivable-mark-installments-error" v-if="quantityMessage">
                        {{ quantityMessage }}
                    </span>
                </b-col>
                <b-col xs="12" sm="12" md="6">
                    <DateTime v-model="payableReceivableMark.conciliationDate" :title="'Data de Conciliação'"
                        field="conciliationDate" :formName="formName" :format="'DD/MM/YYYY'"
                        :placeholder="'Selecione a data de conciliação'" />
                </b-col>
            </b-row>

            <div class="payable-receivable-mark-installments-list" v-if="totalOpen">
                <div class="payable-receivable-mark-installments-item" v-for="item in items" :key="item.id"
                    :class="{ 'payable-receivable-mark-installments-item-selected': isSelected(item) }">
                    <span class="payable-receivable-mark-installments-number">{{ item.installments }}</span>
                    <span class="payable-receivable-mark-installments-due">{{ item.dueDate }}</span>
                    <span class="payable-receivable-mark-installments-value">{{ item.value | currency }}</span>
                </div>
            </div>

            <div class="payable-receivable-mark-installments-total" v-if="selectedItems.length">
                <span>Total a quitar</span>
                <b>{{ selectedTotal | currency }}</b>
            </div>

            <Select title="Categoria" field="category" :formName="formName"
                url="api/v1/finance/chart-of-accounts/select-all" :propsParams="{ any: typeCostCenter }"
                v-model="payableReceivableMark.chartOfAccounts" :showNewRegister="false">
            </Select>

            <b-row>
                <b-col xs="12" sm="12" md="6">
                    <Select title="Tipo de Pagamento" field="typePayment" :formName="formName"
                        placeholder="Selecione o tipo de pagamento" v-model="payableReceivableMark.typePayment"
                        url="/api/v1/finance/type-payment/select-all" nameNewRegister="typePaymentCreateUpdate"
                        titleNewRegister="Tipo de Pagamento" :widthNewRegister="500" :heightNewRegister="250">
                        <TypePaymentCreateUpdate :registerInSelect="true" v-model="payableReceivableMark.typePayment" />
                    </Select>
                </b-col>
                <b-col xs="12" sm="12" md="6">
                    <Select title="Conta Bancária" field="bankAccount" :formName="formName"
                        placeholder="Selecione a conta bancária" v-model="payableReceivableMark.bankAccount"
                        url="api/v1/finance/bank-account/select-all" :showNewRegister="false">
                    </Select>
                </b-col>
            </b-row>
        </div>
    </div>
</template>

<script>
import Alert from "@/ui/layout/Alert";
import DateTime from "@/ui/forms/DateTime";
import InputNumber from "@/ui/forms/InputNumber";
import Select from "@/ui/forms/Select";
import Loading from "@/ui/shared/Loading.vue";
import TypePaymentCreateUpdate from "@/modules/finance/components/typePayment/TypePaymentCreateUpdate.vue";

import PayableReceivableService from "../../services/payableReceivable.service.js";
import { getPayableReceivableTypeCostCenter } from "../../services/payableReceivableList.config";

import { mapState } from "vuex";

export default {
    name: "PayableReceivableMarkInstallments",
    components: {
        Alert,
        DateTime,
        InputNumber,
        Select,
        Loading,
        TypePaymentCreateUpdate,
    },
    data() {
        return {
            formName: "payableReceivableMarkInstallmentsForm",
            loading: true,
            type: null,
            quantity: 1,
            totalOpen: 0,
            items: [],
            payableReceivableMark: {
                conciliationDate: "",
                chartOfAccounts: { id: "", content: "" },
                typePayment: { id: "", content: "" },
                bankAccount: { id: "", content: "" },
            },
        };
    },
    mounted() {
        this.$emit("valid", false);
        this.loadRecord();
    },
    computed: {
        ...mapState("generic", ["selected"]),
        selectedId() {
            return this.selected[0];
        },
        typeCostCenter() {
            return getPayableReceivableTypeCostCenter(this.type);
        },
        selectedItems() {
            if (!this.isQuantityValid) return [];
            return this.items.slice(-this.quantity);
        },
        selectedTotal() {
            return this.selectedItems.reduce((total, item) => total + Number(item.value || 0), 0);
        },
        isQuantityValid() {
            return this.totalOpen > 0 && this.quantity >= 1 && this.quantity <= this.totalOpen;
        },
        quantityMessage() {
            if (!this.totalOpen) return "Não existe parcela em aberto neste parcelamento.";
            if (this.isQuantityValid) return "";
            return `Informe uma quantidade entre 1 e ${this.totalOpen} parcelas em aberto.`;
        },
    },
    methods: {
        async loadRecord() {
            const id = this.selectedId;
            if (!id) return;

            this.loading = true;
            await Promise.all([this.loadAccounts(id), this.loadInstallments(id)]);
            this.loading = false;
            this.emitInput();
        },
        async loadAccounts(id) {
            const response = await PayableReceivableService.getAllPartialPayment(id);
            if (!response.success) return;

            const payableReceivable = response.content.payableReceivable || {};

            this.type = payableReceivable.type ?? null;
            this.payableReceivableMark.chartOfAccounts = this.toSelectItem(payableReceivable.chartOfAccounts);
            this.payableReceivableMark.typePayment = this.toSelectItem(payableReceivable.typePayment);
            this.payableReceivableMark.bankAccount = this.toSelectItem(payableReceivable.bankAccount);
        },
        async loadInstallments(id) {
            const response = await PayableReceivableService.getOpenInstallments(id);
            if (!response.success) return;

            this.items = response.content.items || [];
            this.totalOpen = response.content.totalOpen || 0;
        },
        toSelectItem(item) {
            return item?.id ? { id: item.id, content: item.content } : { id: "", content: "" };
        },
        isSelected(item) {
            return this.selectedItems.some((selected) => selected.id === item.id);
        },
        emitInput() {
            this.$emit("input", {
                quantity: this.quantity,
                conciliationDate: this.payableReceivableMark.conciliationDate,
                chartOfAccounts: this.payableReceivableMark.chartOfAccounts,
                typePayment: this.payableReceivableMark.typePayment,
                bankAccount: this.payableReceivableMark.bankAccount,
            });
            this.$emit("valid", this.isQuantityValid);
        },
    },
    watch: {
        payableReceivableMark: {
            handler() {
                this.emitInput();
            },
            deep: true,
        },
        quantity() {
            this.emitInput();
        },
    },
};
</script>

<style scoped>
.payable-receivable-mark-installments-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 4px 0;
}

.payable-receivable-mark-installments-list {
    display: flex;
    flex-direction: column;
    border: 1px solid var(--border-color);
    border-radius: 8px;
    overflow: hidden;
    margin-bottom: 8px;
}

.payable-receivable-mark-installments-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 8px 12px;
    font-size: 13px;
    color: var(--text-secondary);
    border-bottom: 1px solid var(--border-subtle);
}

.payable-receivable-mark-installments-item:last-child {
    border-bottom: none;
}

.payable-receivable-mark-installments-item-selected {
    background: var(--bg-elevated);
    color: var(--text-primary);
    font-weight: 600;
}

.payable-receivable-mark-installments-number {
    min-width: 60px;
}

.payable-receivable-mark-installments-value {
    text-align: right;
}

.payable-receivable-mark-installments-total {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    font-size: 14px;
    color: var(--text-primary);
    background: var(--bg-elevated);
    border: 1px solid var(--border-subtle);
    border-radius: 8px;
    margin-bottom: 8px;
}

.payable-receivable-mark-installments-error {
    display: block;
    color: var(--color-danger);
    font-size: 13px;
    margin-top: -4px;
}
</style>
